/** CMP Document Assistant rich panel.
 *
 *  Shows the OCR transcription side-by-side with the original PDF
 *  (PDF is rendered by SourcePdfCard in the right rail of the page
 *  already, so this panel focuses on the text and quality signals).
 *
 *  Banner severity is tiered against actual extraction quality so a
 *  92%-clean document isn't styled identically to one that failed:
 *    - red    : empty text OR coverage < 80%
 *    - amber  : coverage 80-99% / extraction_complete=false / has gaps
 *    - green  : 100% coverage AND extraction_complete=true AND no warnings
 *  In every non-green case the reviewer must still judge before approval —
 *  the colour just signals how alarming the gap is.
 */
import { AlertTriangle, CheckCircle2, FileWarning, Info, PenLine, Stamp } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

interface HandwrittenRegion {
  page?: number;
  location?: string;
  transcribed_as?: string;
  legible?: boolean;
}

interface UnreadableRegion {
  page?: number;
  location?: string;
  reason?: string;
  estimated_chars_lost?: number;
}

interface StampOrSeal {
  page?: number;
  location?: string;
  legible_text?: string;
}

const asNumber = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const asBool = (v: unknown): boolean => v === true;
const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
function asObjArray<T>(v: unknown): T[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x) => x !== null && typeof x === 'object') as T[];
}

export function RichDocumentAssistantBody({
  output,
}: {
  output: Record<string, unknown>;
}) {
  const text = typeof output.text === 'string' ? output.text : '';
  const pages = asNumber(output.page_count);
  const coverage = asNumber(output.coverage_percent);
  const extractionComplete = asBool(output.extraction_complete);
  const warnings = asStringArray(output.warnings);
  const handwritten = asObjArray<HandwrittenRegion>(output.handwritten_regions);
  const unreadable = asObjArray<UnreadableRegion>(output.unreadable_regions);
  const stamps = asObjArray<StampOrSeal>(output.stamps_and_seals);

  const allClean = extractionComplete && coverage >= 100 && warnings.length === 0;
  const isHardFailure = text.length === 0 || coverage < 80;
  const hasGaps =
    !allClean && (!extractionComplete || coverage < 100 || unreadable.length > 0 || warnings.length > 0);

  const metaBadges = (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className="bg-background">
        Coverage: {coverage}% / 100%
      </Badge>
      <Badge variant="outline" className="bg-background">
        extraction_complete: {String(extractionComplete)}
      </Badge>
      {pages > 0 && (
        <Badge variant="outline" className="bg-background">
          {pages} page{pages === 1 ? '' : 's'}
        </Badge>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Banner severity is tiered against actual extraction quality. */}
      {isHardFailure ? (
        <Alert variant="destructive" className="border-2">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          <AlertTitle className="font-bold">
            {text.length === 0
              ? 'OCR returned no transcription — manual handling required'
              : `Document processed unreliably — only ${coverage}% coverage`}
          </AlertTitle>
          <AlertDescription className="space-y-2 text-sm">
            {metaBadges}
            {warnings.length > 0 && (
              <ul className="list-disc list-inside space-y-1">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      ) : hasGaps ? (
        <Alert className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
          <Info className="h-5 w-5 text-amber-700 dark:text-amber-300" aria-hidden="true" />
          <AlertTitle className="font-bold text-amber-900 dark:text-amber-100">
            Document processed with gaps — review the highlighted regions before approval
          </AlertTitle>
          <AlertDescription className="space-y-2 text-sm text-amber-900/90 dark:text-amber-100/90">
            {metaBadges}
            {warnings.length > 0 && (
              <ul className="list-disc list-inside space-y-1">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      ) : allClean ? (
        <Alert className="border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950/30">
          <CheckCircle2 className="h-5 w-5 text-green-700 dark:text-green-300" aria-hidden="true" />
          <AlertTitle className="text-green-900 dark:text-green-100">
            Document transcribed in full — 100% coverage
          </AlertTitle>
          <AlertDescription className="text-sm text-green-900/80 dark:text-green-100/80">
            {pages} page{pages === 1 ? '' : 's'} processed without warnings. Still
            compare the text against the PDF before approval — agent confidence is
            not certainty.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Handwritten regions */}
      {handwritten.length > 0 && (
        <div className="rounded border bg-card p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
            <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
            Handwritten content ({handwritten.length})
          </p>
          <ul className="space-y-1.5 text-sm">
            {handwritten.map((h, i) => (
              <li key={i} className="border-l-2 pl-2">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="text-[10px]">Page {h.page ?? '?'}</Badge>
                  <span>{h.location ?? '—'}</span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${h.legible ? 'bg-green-50 text-green-800 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}
                  >
                    {h.legible ? 'legible' : 'illegible'}
                  </Badge>
                </div>
                {h.transcribed_as && (
                  <p className="mt-0.5 italic">&ldquo;{h.transcribed_as}&rdquo;</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unreadable regions — every entry is a reason coverage isn't 100%. */}
      {unreadable.length > 0 && (
        <div className="rounded border border-destructive bg-destructive/5 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-destructive mb-2 flex items-center gap-1.5">
            <FileWarning className="h-3.5 w-3.5" aria-hidden="true" />
            Unreadable regions ({unreadable.length})
          </p>
          <ul className="space-y-1.5 text-sm">
            {unreadable.map((u, i) => (
              <li key={i} className="border-l-2 border-destructive pl-2">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="text-[10px]">Page {u.page ?? '?'}</Badge>
                  <span>{u.location ?? '—'}</span>
                  {u.estimated_chars_lost ? (
                    <Badge variant="outline" className="text-[10px] bg-destructive/10">
                      ~{u.estimated_chars_lost} chars lost
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-0.5">{u.reason ?? 'unspecified'}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stamps / seals */}
      {stamps.length > 0 && (
        <div className="rounded border bg-card p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
            <Stamp className="h-3.5 w-3.5" aria-hidden="true" />
            Stamps &amp; seals ({stamps.length})
          </p>
          <ul className="space-y-1 text-sm">
            {stamps.map((s, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" className="text-[10px]">Page {s.page ?? '?'}</Badge>
                <span className="text-muted-foreground">{s.location ?? '—'}</span>
                {s.legible_text && <span className="italic">&ldquo;{s.legible_text}&rdquo;</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The transcription itself. Editable via the StageReviewPanel's
          "Edit before approve" affordance (binds to output.text per
          STAGE_SPECS.document_assistant.edit). */}
      <div className="rounded border bg-card">
        <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/30">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Transcription (compare against the PDF on the right)
          </p>
          <span className="text-[10px] text-muted-foreground">
            {text.length.toLocaleString()} characters
          </span>
        </div>
        <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap font-sans text-sm leading-relaxed px-3 py-2">
          {text || 'No transcription returned by the agent.'}
        </pre>
      </div>
    </div>
  );
}
